import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Banner, PageHeader, Skeleton, toast } from "@optymyse/suite-ui";
import { useCompanies, useContact, useUpdateContact } from "../lib/directory";
import { fill, strings } from "../lib/strings";
import { ApiError } from "../lib/api";
import { DirectoryProblem, Page, Panel } from "../components/directory/Page";
import { changeOf, ContactForm, draftOf, type CompanyChoice } from "../components/directory/ContactForm";
import type { FormProblem } from "../components/directory/CompanyForm";

/**
 * A contact's change, made to the version the page read (If-Match), as a customer's is. Their ways to reach them and
 * their customers are sent whole when any changed, and Link gives every method a new id at each save.
 */
function EditContactPage() {
  const { contactId } = Route.useParams();
  const navigate = useNavigate();
  const contact = useContact(contactId);
  const companies = useCompanies();
  const update = useUpdateContact(contactId);
  const [problem, setProblem] = useState<FormProblem | null>(null);
  const [conflict, setConflict] = useState(false);
  const back = () => void navigate({ to: "/admin/contacts/$contactId", params: { contactId } });

  if (contact.isPending)
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96" />
      </Page>
    );
  if (contact.isError)
    return (
      <Page>
        <DirectoryProblem
          {...(contact.error instanceof ApiError ? { message: contact.error.message } : {})}
          onRetry={() => void contact.refetch()}
        />
      </Page>
    );

  const record = contact.data;
  // The directory's customers, and those the contact is linked to already, which a capped list may not hold.
  const choices = new Map<string, CompanyChoice>(
    (companies.data?.items ?? []).map((row) => [row.id, { id: row.id, name: row.display_name, detail: row.domain }]),
  );
  for (const link of record.companies)
    if (!choices.has(link.company_id))
      choices.set(link.company_id, { id: link.company_id, name: link.company_name, detail: null });

  return (
    <Page>
      <PageHeader
        breadcrumb={[
          { label: strings.contacts.title, href: "/admin/contacts" },
          { label: record.display_name, href: `/admin/contacts/${contactId}` },
          { label: strings.contacts.page.edit },
        ]}
        title={fill(strings.contacts.form.editTitle, { name: record.display_name })}
      />
      {conflict && (
        <Banner tone="warning" title={strings.contacts.form.conflictTitle} role="alert">
          {strings.contacts.form.conflict}
        </Banner>
      )}
      <Panel>
        <ContactForm
          key={record.version}
          initial={draftOf(record)}
          companies={[...choices.values()]}
          submitLabel={strings.contacts.form.save}
          pending={update.isPending}
          problem={problem}
          onCancel={back}
          onSubmit={(draft) => {
            const change = changeOf(record, draft);
            if (Object.keys(change).length === 0) {
              setProblem({ message: strings.contacts.form.nothingChanged, field: null });
              return;
            }
            setProblem(null);
            setConflict(false);
            update.mutate(
              { change, version: record.version },
              {
                onSuccess: (saved) => {
                  toast({ title: fill(strings.contacts.form.saved, { name: saved.display_name }) });
                  back();
                },
                onError: (error) => {
                  if (error instanceof ApiError && error.status === 412) {
                    setConflict(true);
                    return;
                  }
                  setProblem({ message: error.message, field: error instanceof ApiError ? error.field : null });
                },
              },
            );
          }}
        />
      </Panel>
    </Page>
  );
}

export const Route = createFileRoute("/admin/contacts/$contactId/edit")({ component: EditContactPage });
